import { hasNativeBridge } from "./native-bridge";
import { publishVoiceStats } from "./audio-debug";

function num(row: Record<string, unknown>, key: string): number | undefined {
  const v = row[key];
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

type CaptureFrame = {
  g711: Uint8Array;
  t: number;
  seq: number;
};

type CaptureHandler = (frame: CaptureFrame) => void;

let captureHandler: CaptureHandler | null = null;
let available: boolean | null = null;
let playbackStarted = false;

function flutterHandler(): { callHandler: (name: string, ...args: unknown[]) => Promise<unknown> } | null {
  if (typeof window === "undefined") return null;
  const bridge = (window as unknown as { flutter_inappwebview?: { callHandler?: (name: string, ...args: unknown[]) => Promise<unknown> } })
    .flutter_inappwebview;
  return bridge?.callHandler ? { callHandler: bridge.callHandler.bind(bridge) } : null;
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function bytesToB64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function installReceiver(): void {
  if (typeof window === "undefined") return;
  (window as unknown as { __dashcamNativeVoice?: { onCapture: (row: Record<string, unknown>) => void } }).__dashcamNativeVoice = {
    onCapture(row: Record<string, unknown>) {
      const b64 = typeof row.g711 === "string" ? row.g711 : "";
      if (!b64 || !captureHandler) return;
      captureHandler({
        g711: b64ToBytes(b64),
        t: typeof row.t === "number" ? row.t : Date.now(),
        seq: typeof row.seq === "number" ? row.seq : 0,
      });
      publishVoiceStats({
        captureMode: "native",
        captureSampleRate: num(row, "captureSampleRate") ?? 8000,
        captureChannelCount: num(row, "captureChannelCount") ?? 1,
        captureEncoding: typeof row.captureEncoding === "string" ? row.captureEncoding : "pcm16",
        pcmFrameSize: num(row, "pcmFrameSize") ?? 160,
        resampleIn: num(row, "resampleIn") ?? 0,
        resampleOut: num(row, "resampleOut") ?? 0,
        g711Codec: typeof row.g711Codec === "string" ? row.g711Codec : "pcma",
        g711FrameBytes: num(row, "g711FrameBytes") ?? 160,
        pcmMin: num(row, "pcmMin") ?? 0,
        pcmMax: num(row, "pcmMax") ?? 0,
        pcmRMS: num(row, "pcmRMS") ?? 0,
        clippingCount: num(row, "clippingCount") ?? 0,
        captureAt: num(row, "t") ?? Date.now(),
        captureRate: num(row, "captureSampleRate") ?? 8000,
        captureBlock: 160,
      });
    },
  };
}

installReceiver();

export async function nativeVoiceStatus(): Promise<Record<string, unknown> | null> {
  const native = flutterHandler();
  if (!native) return null;
  try {
    const row = (await native.callHandler("voiceStatus")) as Record<string, unknown> | null;
    return row && typeof row === "object" ? row : null;
  } catch {
    return null;
  }
}

export async function nativeVoiceAvailable(): Promise<boolean> {
  if (available != null) return available;
  if (!hasNativeBridge()) {
    available = false;
    return false;
  }
  const native = flutterHandler();
  if (!native) {
    available = false;
    return false;
  }
  try {
    const row = (await native.callHandler("voiceStatus")) as { ok?: boolean; capture?: boolean; playback?: boolean } | null;
    available = Boolean(row && row.ok && row.capture && row.playback);
  } catch {
    available = false;
  }
  publishVoiceStats({ captureMode: available ? "native" : "none" });
  return available;
}

export function onNativeCapture(handler: CaptureHandler | null): void {
  captureHandler = handler;
}

export async function armNativeCapture(codec: "pcma" | "pcmu"): Promise<boolean> {
  const native = flutterHandler();
  if (!native) return false;
  try {
    const row = (await native.callHandler("voiceArmCapture", { codec })) as { ok?: boolean } | null;
    return row?.ok === true;
  } catch {
    return false;
  }
}

export async function startNativeCapture(codec: "pcma" | "pcmu"): Promise<boolean> {
  const native = flutterHandler();
  if (!native) return false;
  try {
    const row = (await native.callHandler("voiceStartCapture", { codec })) as { ok?: boolean } | null;
    return row?.ok === true;
  } catch {
    return false;
  }
}

export function setNativeTransmit(on: boolean): void {
  const native = flutterHandler();
  if (!native) return;
  void native.callHandler("voiceSetTransmit", { on }).catch(() => undefined);
}

export async function drainNativeCapture(): Promise<Uint8Array[]> {
  const native = flutterHandler();
  if (!native) return [];
  try {
    const row = (await native.callHandler("voiceDrainCapture")) as { frames?: unknown } | null;
    const frames = row?.frames;
    if (!Array.isArray(frames)) return [];
    const out: Uint8Array[] = [];
    for (const item of frames) {
      if (typeof item !== "string" || !item) continue;
      const bytes = b64ToBytes(item);
      if (bytes.length === 160) out.push(bytes);
    }
    return out;
  } catch {
    return [];
  }
}

export function setNativeCodec(codec: "pcma" | "pcmu"): void {
  const native = flutterHandler();
  if (!native) return;
  void native.callHandler("voiceSetCodec", { codec }).catch(() => undefined);
}

export async function stopNativeCapture(): Promise<void> {
  const native = flutterHandler();
  if (!native) return;
  try {
    await native.callHandler("voiceStopCapture", {});
  } catch {
    /* ignore */
  }
}

export async function startNativePlayback(): Promise<boolean> {
  const native = flutterHandler();
  if (!native) return false;
  try {
    const row = (await native.callHandler("voiceStartPlayback", {})) as { ok?: boolean } | null;
    playbackStarted = row?.ok === true;
    return playbackStarted;
  } catch {
    return false;
  }
}

export async function stopNativePlayback(): Promise<void> {
  playbackStarted = false;
  const native = flutterHandler();
  if (!native) return;
  try {
    await native.callHandler("voiceStopPlayback", {});
  } catch {
    /* ignore */
  }
}

export function setNativeMuted(muted: boolean): void {
  const native = flutterHandler();
  if (!native) return;
  void native.callHandler("voiceSetMuted", { muted }).catch(() => undefined);
}

export function pushNativeG711(g711: Uint8Array, codec: "pcma" | "pcmu"): void {
  const native = flutterHandler();
  if (!native || !g711.length) return;
  void native.callHandler("voicePushG711", { g711: bytesToB64(g711), codec }).catch(() => undefined);
}
